/**
 * Truth Layer → Compute
 * Developer-only controls for profitability and utilisation projections.
 */
import { useState } from 'react';
import { Cpu } from 'lucide-react';
import { useProfitabilityDateRange } from '../hooks/useProfitabilityDateRange';
import { useTruthProfitabilityPanel } from '../hooks/useTruthProfitabilityPanel';
import { useTruthUtilisationPanel } from '../hooks/useTruthUtilisationPanel';
import ProfitabilityDateRangePicker from '../components/profitability/ProfitabilityDateRangePicker';
import TruthProfitabilityPipelinePanel from '../components/profitability/TruthProfitabilityPipelinePanel';
import TruthUtilisationPipelinePanel from '../components/profitability/TruthUtilisationPipelinePanel';
import ProfitabilityAssumptionsPanel from '../components/profitability/ProfitabilityAssumptionsPanel';
import UtilisationAssumptionsPanel from '../components/profitability/UtilisationAssumptionsPanel';

export default function TruthCompute() {
  const dateRange = useProfitabilityDateRange();
  const [showProfitAssumptions, setShowProfitAssumptions] = useState(false);
  const [showUtilAssumptions, setShowUtilAssumptions] = useState(false);

  const truthPipeline = useTruthProfitabilityPanel({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    includeCoverage: false,
  });

  const utilisationPipeline = useTruthUtilisationPanel({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  return (
    <div className="space-y-6 w-full max-w-5xl">
      <header>
        <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
          <Cpu className="w-7 h-7 text-chart-10" />
          Compute
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Developer tools for truth-layer projections.
        </p>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-chart-10/40 bg-card/80 px-3 py-2.5">
        <div>
          <p className="text-xs font-medium text-foreground">Compute period</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Both pipelines use this range. Run profitability before utilisation when revenue is needed.
          </p>
        </div>
        <ProfitabilityDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onStartDateChange={dateRange.setStartDate}
          onEndDateChange={dateRange.setEndDate}
          onReset={dateRange.resetToDefault}
        />
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground tracking-wide uppercase">
          Profitability
        </h2>
        <TruthProfitabilityPipelinePanel
          panel={truthPipeline}
          assumptionsActive={showProfitAssumptions}
          onAssumptionsClick={() => {
            setShowUtilAssumptions(false);
            setShowProfitAssumptions((v) => !v);
          }}
        />
        <ProfitabilityAssumptionsPanel
          visible={showProfitAssumptions}
          onClose={() => setShowProfitAssumptions(false)}
        />
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground tracking-wide uppercase">
          Utilisation
        </h2>
        <TruthUtilisationPipelinePanel
          panel={utilisationPipeline}
          assumptionsActive={showUtilAssumptions}
          onAssumptionsClick={() => {
            setShowProfitAssumptions(false);
            setShowUtilAssumptions((v) => !v);
          }}
        />
        <UtilisationAssumptionsPanel
          visible={showUtilAssumptions}
          onClose={() => setShowUtilAssumptions(false)}
          mode="truth"
        />
      </section>
    </div>
  );
}
